import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { GrowthContentAssetSelectorComponent } from '../../shared/design-system/growth-content-asset-selector.component';
import { GrowthSeoProfileSelectorComponent } from '../../shared/design-system/growth-seo-profile-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthGeoProfile, GrowthSearchService } from './growth-search.service';

interface FactRow {
  key: string;
  value: string;
}

interface SourceRefRow {
  contentAssetId: string | null;
}

@Component({
  standalone: true,
  selector: 'ain-growth-geo-profiles',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    GrowthSeoProfileSelectorComponent,
    GrowthContentAssetSelectorComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    TagModule
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.search.geo.title')" [description]="t('growth.search.geo.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.search.geo.hint.title'"
        [descriptionKey]="'growth.search.geo.hint.description'"
        [bulletKeys]="['growth.search.geo.hint.bullets.sandbox', 'growth.search.geo.hint.bullets.citable', 'growth.search.geo.hint.bullets.seoLink']" />

      <section class="card">
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.search.geo.actions.newProfile')" (click)="openDialog()"></button>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
        </div>
        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else {
          <p-table [value]="profiles()" [rows]="10" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.search.geo.columns.summary') }}</th>
                <th>{{ t('growth.search.geo.columns.seoProfile') }}</th>
                <th>{{ t('growth.search.geo.columns.score') }}</th>
                <th>{{ t('growth.search.geo.columns.provider') }}</th>
                <th>{{ t('growth.search.geo.columns.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.entitySummary }}</td>
                <td>{{ row.seoProfileId }}</td>
                <td>{{ row.lastScore ?? '—' }}</td>
                <td><p-tag severity="warn" [value]="t('growth.search.geo.provider.sandbox')" /></td>
                <td class="actions">
                  <button pButton type="button" size="small" severity="secondary" [label]="t('common.edit')" (click)="edit(row)"></button>
                  <button pButton type="button" size="small" [label]="t('growth.search.geo.actions.runScore')" (click)="runScore(row)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      <p-dialog [header]="t('growth.search.geo.dialog.title')" [visible]="dialogVisible()" (visibleChange)="dialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(860px,95vw)' }">
        <div class="grid">
          <ain-form-field [label]="t('growth.search.geo.fields.seoProfile')">
            <ain-growth-seo-profile-selector [(ngModel)]="model.seoProfileId" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.search.geo.fields.entitySummary')">
            <textarea pInputTextarea rows="3" [(ngModel)]="model.entitySummary"></textarea>
          </ain-form-field>
        </div>

        <div class="section-title"><h4>{{ t('growth.search.geo.sections.facts') }}</h4></div>
        @for (row of factRows; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.search.geo.fields.factKey')"><input pInputText [(ngModel)]="row.key" /></ain-form-field>
            <ain-form-field [label]="t('growth.search.geo.fields.factValue')"><input pInputText [(ngModel)]="row.value" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeFact($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addFact()"></button>

        <div class="section-title"><h4>{{ t('growth.search.geo.sections.sourceRefs') }}</h4></div>
        @for (row of sourceRefRows; track $index) {
          <div class="row-grid source-row">
            <ain-form-field [label]="t('growth.search.geo.fields.contentAsset')">
              <ain-growth-content-asset-selector [(ngModel)]="row.contentAssetId" />
            </ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeSourceRef($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addSourceRef()"></button>

        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit,minmax(220px,1fr)); }
    .toolbar,.actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .section-title { margin-top: 8px; }
    .row-grid { display: grid; gap: 8px; grid-template-columns: minmax(180px,1fr) minmax(180px,1fr) auto; align-items: end; margin-bottom: 8px; }
    .source-row { grid-template-columns: minmax(260px,1fr) auto; }
  `]
})
export class GrowthGeoProfilesComponent implements OnInit {
  private readonly service = inject(GrowthSearchService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly profiles = signal<GrowthGeoProfile[]>([]);
  readonly dialogVisible = signal(false);
  selectedId: string | null = null;
  model = { seoProfileId: null as string | null, entitySummary: '' };
  factRows: FactRow[] = [{ key: '', value: '' }];
  sourceRefRows: SourceRefRow[] = [{ contentAssetId: null }];

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.service.listGeoProfiles().subscribe({
      next: rows => {
        this.profiles.set(rows);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  openDialog(): void {
    this.selectedId = null;
    this.model = { seoProfileId: null, entitySummary: '' };
    this.factRows = [{ key: '', value: '' }];
    this.sourceRefRows = [{ contentAssetId: null }];
    this.dialogVisible.set(true);
  }

  edit(row: GrowthGeoProfile): void {
    this.selectedId = row.id;
    this.model = { seoProfileId: row.seoProfileId, entitySummary: row.entitySummary };
    this.factRows = this.parseFacts(row.factsJson);
    this.sourceRefRows = this.parseSourceRefs(row.sourceRefsJson);
    if (this.factRows.length === 0) this.factRows = [{ key: '', value: '' }];
    if (this.sourceRefRows.length === 0) this.sourceRefRows = [{ contentAssetId: null }];
    this.dialogVisible.set(true);
  }

  save(): void {
    if (!this.model.seoProfileId) return;
    this.service.upsertGeoProfile({
      id: this.selectedId,
      seoProfileId: this.model.seoProfileId,
      entitySummary: this.model.entitySummary,
      factsJson: JSON.stringify(this.factsToObject(this.factRows)),
      sourceRefsJson: JSON.stringify(this.sourceRefRows.map(row => row.contentAssetId).filter(Boolean))
    }).subscribe({
      next: () => {
        this.dialogVisible.set(false);
        this.load();
      }
    });
  }

  runScore(row: GrowthGeoProfile): void {
    this.service.runGeoScore(row.id, {
      providerMode: 'sandbox',
      score: row.lastScore ?? 0.74,
      signalsJson: '[{"signal":"entity-coverage","status":"ok"}]'
    }).subscribe({ next: () => this.load() });
  }

  addFact(): void { this.factRows.push({ key: '', value: '' }); }
  removeFact(index: number): void { this.factRows.splice(index, 1); }
  addSourceRef(): void { this.sourceRefRows.push({ contentAssetId: null }); }
  removeSourceRef(index: number): void { this.sourceRefRows.splice(index, 1); }

  private factsToObject(rows: FactRow[]): Record<string, string> {
    const object: Record<string, string> = {};
    rows.forEach(row => {
      const key = row.key.trim();
      if (!key) return;
      object[key] = row.value.trim();
    });
    return object;
  }

  private parseFacts(json: string): FactRow[] {
    try {
      const parsed = JSON.parse(json) as unknown;
      if (Array.isArray(parsed))
        return parsed.filter(item => item && typeof item === 'object').map(item => {
          const row = item as Record<string, unknown>;
          return { key: String(row['key'] ?? row['fact'] ?? ''), value: String(row['value'] ?? '') };
        });
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed))
        return Object.entries(parsed as Record<string, unknown>).map(([key, value]) => ({ key, value: value == null ? '' : String(value) }));
      return [];
    } catch {
      return [];
    }
  }

  private parseSourceRefs(json: string): SourceRefRow[] {
    try {
      const parsed = JSON.parse(json) as unknown;
      if (!Array.isArray(parsed)) return [{ contentAssetId: null }];
      return parsed.map(item => ({ contentAssetId: item == null ? null : String(item) }));
    } catch {
      return [{ contentAssetId: null }];
    }
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }
}
